import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type UIEvent,
} from 'react';
import type { SearchColumn } from './types';

/** Column/row selection + keyboard/hover/scroll sync for the omni palette. */
export function useOmniSelection(columns: SearchColumn[], open: boolean, listId: string) {
  const [colIndex, setColIndex] = useState(0);
  const [rowIndex, setRowIndex] = useState(0);
  const ignoreHoverRef = useRef(false);
  const suppressScrollSyncRef = useRef(false);
  const scrollSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetSelection = () => {
    setColIndex(0);
    setRowIndex(0);
    ignoreHoverRef.current = false;
  };

  // Keep the selection inside the results when they change shape.
  const [clampedFor, setClampedFor] = useState(columns);
  if (clampedFor !== columns) {
    setClampedFor(columns);
    if (columns.length === 0) {
      setColIndex(0);
      setRowIndex(0);
    } else {
      setColIndex((c) => Math.min(c, columns.length - 1));
      setRowIndex((r) => {
        const col = columns[Math.min(colIndex, columns.length - 1)];
        if (!col || col.items.length === 0) return 0;
        return Math.min(r, col.items.length - 1);
      });
    }
  }

  useEffect(() => {
    if (!open || columns.length === 0) return;
    const optionId = `${listId}-c${colIndex}-r${rowIndex}`;
    const el = document.getElementById(optionId);
    const colEl = document.getElementById(`${listId}-col-${colIndex}`);
    suppressScrollSyncRef.current = true;
    colEl?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
    el?.scrollIntoView({ block: 'nearest' });
    if (scrollSyncTimerRef.current) clearTimeout(scrollSyncTimerRef.current);
    scrollSyncTimerRef.current = setTimeout(() => {
      suppressScrollSyncRef.current = false;
    }, 350);
  }, [colIndex, rowIndex, open, listId, columns.length]);

  const activate = () => {
    columns[colIndex]?.items[rowIndex]?.onSelect();
  };

  const onDialogKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (columns.length === 0) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      ignoreHoverRef.current = true;
      const len = columns[colIndex]?.items.length ?? 0;
      if (len === 0) return;
      setRowIndex((r) =>
        e.key === 'ArrowDown' ? (r + 1) % len : (r - 1 + len) % len
      );
      return;
    }

    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      ignoreHoverRef.current = true;
      setColIndex((c) => {
        const next =
          e.key === 'ArrowRight'
            ? (c + 1) % columns.length
            : (c - 1 + columns.length) % columns.length;
        const nextLen = columns[next]?.items.length ?? 0;
        setRowIndex((r) => Math.min(r, Math.max(nextLen - 1, 0)));
        return next;
      });
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      activate();
    }
  };

  const onScrollerScroll = (e: UIEvent<HTMLDivElement>) => {
    if (suppressScrollSyncRef.current || columns.length === 0) return;
    const scroller = e.currentTarget;
    const mid = scroller.scrollLeft + scroller.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < columns.length; i++) {
      const col = document.getElementById(`${listId}-col-${i}`);
      if (!col) continue;
      const center = col.offsetLeft + col.offsetWidth / 2;
      const dist = Math.abs(center - mid);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    if (best !== colIndex) {
      setColIndex(best);
      setRowIndex((r) =>
        Math.min(r, Math.max((columns[best]?.items.length ?? 1) - 1, 0))
      );
    }
  };

  return {
    colIndex,
    rowIndex,
    setColIndex,
    setRowIndex,
    resetSelection,
    ignoreHoverRef,
    onDialogKeyDown,
    onScrollerScroll,
    activate,
  };
}
